import { Collection } from '@msw/data';
import { z } from 'zod';

import { DEMO_PRICING_TYPES, DemoConfig, normalizePricingType } from '@/demo/demoConfig';
import { buildPlayableInvoices } from '@/demo/demoTransform';

/**
 * Composite lookup key for a taker-market slot. @msw/data v1 has no primaryKey() helper,
 * so we enforce uniqueness by convention — every create/findFirst goes through this key.
 */
export function marketKey(marketUuid: string, supplierDivisionUuid: string): string {
  return `${marketUuid}:${supplierDivisionUuid}`;
}

/**
 * In-memory normalized store for taker-market invoice stats.
 *
 * Why a separate collection instead of reusing DemoTakerMarket or SupplierMarketsResponse?
 * - DemoTakerMarket (demoConfig.ts) is the raw config shape: it carries identity/display fields
 *   (makerOrganizationName, takerDivisionName, makerId, ...) that never change and are re-read from
 *   config at serialization time. Storing them here would duplicate static data unnecessarily.
 * - SupplierMarketsResponse is a deeply nested SEA API response shape built for wire serialization.
 *   It embeds computed/joined data (offer block, group metadata) that must be assembled fresh on
 *   every read. Storing it in a collection would mean persisting derived data and keeping it in sync
 *   manually — the wrong level of abstraction for a mutable store.
 *
 * This collection holds only the fields the serialization layer (demoSerialize.ts) needs to:
 *   1. compute invoice stats (eligible/excluded counts and amounts for each market slot)
 *   2. build the `/offers` stats.invoice block after an offer is submitted
 *   3. look up maxApr for pricing-suggestion rate caps
 *
 * The `key` field is the composite `${marketUuid}:${supplierDivisionUuid}` used by offers as a
 * foreign key and by findFirst queries as the de-facto primary key.
 */
export const takerMarkets = new Collection({
  schema: z.object({
    key: z.string(),
    // market identity
    marketUuid: z.string(),
    marketId: z.number(),
    marketEnabled: z.boolean(),
    marketingDisplayName: z.string(),
    marketType: z.string(),
    // Pricing type — drives meta.pricingType and offerOptions. Enforced at the schema level (rather
    // than `z.string()` + a downstream cast) so an invalid value fails validation here instead of
    // silently flowing into the serialized API response as an unsupported string.
    pricingType: z.enum(DEMO_PRICING_TYPES),
    nextClearDate: z.string().nullable(),
    payDate: z.string().nullable(),
    maxApr: z.number().nullable(),
    maxDiscount: z.number().nullable(),
    isDsf: z.boolean(),
    trendingRate: z.number().nullable(),
    expressAcceptRate: z.number().nullable(),
    rateType: z.enum(['APR', 'DISC']),
    // Benchmark rate fields — null for non-benchmark markets.
    rateName: z.string().nullable(),
    referenceRate: z.number().nullable(),
    spread: z.number().nullable(),
    // supplier (taker) identity
    supplierDivisionUuid: z.string(),
    supplierDivisionId: z.number(),
    supplierDivisionName: z.string(),
    supplierBuyerId: z.string(),
    // maker (buyer org) identity
    makerOrganizationUuid: z.string(),
    makerOrganizationName: z.string(),
    makerDivisionId: z.number(),
    makerDivisionName: z.string(),
    // invoice stats
    currency: z.string(),
    eligibleInvoiceCount: z.number(),
    eligibleInvoiceAmount: z.number(),
    eligibleDpeWeightedSum: z.number(),
    eligibleDpeWeightedAverage: z.number(),
    excludedInvoiceCount: z.number(),
    excludedInvoiceAmount: z.number(),
  }),
});

/**
 * In-memory normalized store for demo offers — created on submit, updated on edit.
 *
 * Why a separate collection instead of reusing OffersResponse?
 * - OffersResponse is the full SEA `/offers` wire format: it nests supplierMarkets[], actions[],
 *   editRestrictions, and a stats.invoice block that must be computed from the takerMarket at
 *   read time. Storing it in a collection would embed computed stats as static data, meaning an
 *   edit would require re-computing and re-writing nested sub-objects — fragile and wrong.
 * - This collection stores only the atomic offer fields that the user sets (rate, frequency, status,
 *   etc.) plus the foreign key (`marketKey`) back to takerMarkets. The serialization layer joins
 *   them at read time to produce the correct nested OffersResponse entry.
 *
 * Shape rationale:
 * - `uuid` — stable identifier returned in the POST 201 response and used by PATCH requests.
 * - `marketKey` — foreign key to takerMarkets; drives the join in getOffers() / getSupplierMarkets().
 * - `status` — the single field that makes the dashboard card flip from 'Name Your Rate' to active.
 *   formatSEASupplierMarkets sets offerConfig.isEnabled = seaOffer?.status === 'active'.
 * - `cleared` — false this iteration; the future clearMarket action sets it to true and triggers
 *   the serializer to move eligible stats into accepted/awarded/clearing.
 * - `excludeNewInvoices` / `expirationDate` — nullable; only set when the user configures them.
 */
export const offers = new Collection({
  schema: z.object({
    uuid: z.string(),
    marketKey: z.string(),
    marketUuid: z.string(),
    supplierDivisionUuid: z.string(),
    makerOrganizationUuid: z.string(),
    rateType: z.enum(['APR', 'DISC']),
    rate: z.number(),
    frequency: z.enum(['one-time', 'recurring']),
    status: z.enum(['active', 'paused', 'inactive', 'unknown']),
    excludeNewInvoices: z.boolean().nullable(),
    expirationDate: z.string().nullable(),
    // Benchmark rate snapshot — null for non-benchmark offers.
    rateName: z.string().nullable(),
    referenceRate: z.number().nullable(),
    spread: z.number().nullable(),
    cleared: z.boolean(),
    clearedAt: z.string().nullable(),
    // Snapshotted at clear time from the live invoices collection so post-clear reads
    // use the correct included-invoice values regardless of subsequent exclusion changes.
    clearedEligibleCount: z.number().nullable(),
    clearedEligibleAmount: z.number().nullable(),
    clearedEligibleDpeWeightedSum: z.number().nullable(),
    clearedEligibleDpeWeightedAverage: z.number().nullable(),
    clearedExcludedCount: z.number().nullable(),
    clearedExcludedAmount: z.number().nullable(),
  }),
});

/**
 * In-memory store for demo playable invoices, seeded from buildPlayableInvoices on session start.
 *
 * We store invoices in a collection so manageExclusions can toggle `supplierExcluded` per voucher
 * and the serialization layer (getPlayableInvoices / getPlayableInvoiceStats) can compute live
 * stats from the current collection state rather than from the static config snapshot.
 *
 * Fields match PlayableInvoice wire shape, minus the appendDateTimestamp transform applied by the
 * hook — we store raw date strings and let the existing hook transform them on read.
 */
export const invoices = new Collection({
  schema: z.object({
    voucherId: z.string(),
    marketKey: z.string(),
    supplierDivisionUuid: z.string(),
    marketUuid: z.string(),
    currency: z.string(),
    amount: z.number(),
    originalAmount: z.number(),
    originalDepositDate: z.string(),
    daysPaidEarly: z.number(),
    supplierExcluded: z.boolean(),
    // Stamped true at clear time for invoices that were included (supplierExcluded=false) when
    // the market cleared. These invoices are awarded/paid and must not appear in playable invoice
    // responses again, even if supplierExcluded is later toggled by include/exclude actions.
    clearedIncluded: z.boolean(),
  }),
});

let lastConfig: DemoConfig | null = null;

export async function seedDb(config: DemoConfig): Promise<void> {
  lastConfig = config;
  await takerMarkets.clear();
  await offers.clear();
  await invoices.clear();

  for (const tm of config.takerMarkets) {
    const market = config.markets.find((m) => m.uuid === tm.marketUuid);
    await takerMarkets.create({
      key: marketKey(tm.marketUuid, tm.takerUuid),
      // market identity
      marketUuid: tm.marketUuid,
      marketId: tm.marketId,
      marketEnabled: market?.isEnabled ?? true,
      marketingDisplayName: market?.name ?? tm.makerOrganizationName,
      marketType: market?.marketType ?? 'price-discovery',
      // market?.type is an untrusted string from a URL-decoded config — normalize it to a
      // supported value before it hits the z.enum schema above.
      pricingType: normalizePricingType(market?.type),
      nextClearDate: market?.nextClearDate ?? null,
      payDate: market?.payDate ?? null,
      maxApr: market?.maxApr ?? null,
      maxDiscount: market?.maxDiscount ?? null,
      isDsf: market?.isDsf ?? false,
      trendingRate: market?.trendingRate ?? null,
      expressAcceptRate: market?.expressAcceptRate ?? null,
      rateType: market?.rateType ?? 'APR',
      rateName: market?.rateName ?? null,
      referenceRate: market?.referenceRate ?? null,
      spread: market?.spread ?? null,
      // supplier (taker) identity
      supplierDivisionUuid: tm.takerUuid,
      supplierDivisionId: tm.takerId,
      supplierDivisionName: tm.takerDivisionName,
      supplierBuyerId: tm.takerMakerId,
      // maker (buyer org) identity
      makerOrganizationUuid: tm.makerOrganizationUuid,
      makerOrganizationName: tm.makerOrganizationName,
      makerDivisionId: tm.makerId,
      makerDivisionName: tm.makerDivisionName,
      // invoice stats
      currency: tm.currency,
      eligibleInvoiceCount: tm.eligibleInvoiceCount,
      eligibleInvoiceAmount: tm.eligibleInvoiceAmount,
      eligibleDpeWeightedSum: tm.eligibleDpeWeightedSum,
      eligibleDpeWeightedAverage: tm.eligibleDpeWeightedAverage,
      excludedInvoiceCount: tm.takerExcludedInvoiceCount,
      excludedInvoiceAmount: tm.takerExcludedInvoiceAmount,
    });
  }

  const playableInvoicesResponse = buildPlayableInvoices(config);
  for (const inv of playableInvoicesResponse.data.invoices) {
    await invoices.create({
      voucherId: inv.voucherId,
      marketKey: marketKey(inv.marketUuid, inv.supplierDivisionUuid),
      supplierDivisionUuid: inv.supplierDivisionUuid,
      marketUuid: inv.marketUuid,
      currency: inv.currency,
      amount: inv.amount,
      originalAmount: inv.originalAmount,
      originalDepositDate: inv.originalDepositDate ?? '',
      daysPaidEarly: inv.daysPaidEarly,
      supplierExcluded: inv.supplierExcluded ?? false,
      clearedIncluded: false,
    });
  }
}

export async function resetDb(): Promise<void> {
  if (lastConfig) {
    await seedDb(lastConfig);
  }
}
